import './manager-contacts-section.scss'
import React, {
  useState,
} from 'react'
import {
  useTranslation,
} from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Typography,
} from '@mui/material'

import {
  showManagerContactsSummary,
  showManagerRevealContacts,
} from '../detail-visibility'
import ApplicationContactsBlock from './application-contacts-block'
import {
  buildClientContactFields,
  buildExecutorContactFields,
  EXECUTOR_CONTACT_FIELD_KEYS,
  executorContactFormFromApp,
  clientContactsDirty,
  executorContactsDirty,
  shouldShowClientContactsBlock,
} from '../application-contacts-fields'
import {
  clientContactsFieldErrorsFromApi,
  formatApplicationActionError,
  revealContactsFieldErrorsFromApi,
  validateClientContactsForm,
  validateRevealContactsForm,
} from '../reveal-contacts-form'

const ManagerContactsSection = ({
  app,
  managerView,
  contactForm,
  onContactFormChange,
  clientContactForm,
  onClientContactFormChange,
  onSaveClientContacts,
  onSaveExecutorContacts,
  onRevealContacts,
}: ApplicationManagerContactsProps) => {
  const { t } = useTranslation()
  const [ clientFieldErrors, setClientFieldErrors ] = useState<Record<string, string>>({})
  const [ executorFieldErrors, setExecutorFieldErrors ] = useState<RevealContactsFieldErrors>({})
  const [ contactSubmitError, setContactSubmitError ] = useState<string | null>(null)
  const [ isRevealingContacts, setIsRevealingContacts ] = useState(false)
  const [ isSavingClient, setIsSavingClient ] = useState(false)
  const [ isSavingExecutor, setIsSavingExecutor ] = useState(false)

  const clientDirty = clientContactsDirty(clientContactForm, app)
  const executorDirty = executorContactsDirty(contactForm, app)
  const showClientBlock = shouldShowClientContactsBlock(app, managerView)
  const showExecutorEditor = showManagerRevealContacts({ app, managerView })
    || showManagerContactsSummary({ app, managerView })
  const hasSavedExecutorContacts = EXECUTOR_CONTACT_FIELD_KEYS.some((key) => app[key]?.trim())
  const showExecutorBlock = showExecutorEditor || hasSavedExecutorContacts

  const handleClientFieldChange = (key: string, value: string) => {
    setClientFieldErrors((current) => {
      if (!current[key]) {
        return current
      }
      const next = { ...current }
      delete next[key]
      return next
    })
    onClientContactFormChange({
      ...clientContactForm,
      [key]: value,
    } as ClientContactFormValues)
  }

  const handleExecutorFieldChange = (key: string, value: string) => {
    setExecutorFieldErrors((current) => {
      if (!current[key as RevealContactsField]) {
        return current
      }
      const next = { ...current }
      delete next[key as RevealContactsField]
      return next
    })
    onContactFormChange({
      ...contactForm,
      [key]: value,
    } as RevealContactsFormValues)
  }

  const handleSaveClient = async () => {
    const validationErrors = validateClientContactsForm({ form: clientContactForm, t })
    if (Object.keys(validationErrors).length > 0) {
      setClientFieldErrors(validationErrors)
      return
    }
    setClientFieldErrors({})
    setIsSavingClient(true)
    try {
      await onSaveClientContacts()
    } catch (e: unknown) {
      const apiErrors = clientContactsFieldErrorsFromApi((e as { data?: unknown }).data)
      if (Object.keys(apiErrors).length > 0) {
        setClientFieldErrors(apiErrors as Record<string, string>)
      }
    } finally {
      setIsSavingClient(false)
    }
  }

  const handleSaveExecutor = async () => {
    const validationErrors = validateRevealContactsForm({ form: contactForm, t })
    if (Object.keys(validationErrors).length > 0) {
      setExecutorFieldErrors(validationErrors)
      return
    }
    setExecutorFieldErrors({})
    setIsSavingExecutor(true)
    try {
      await onSaveExecutorContacts()
    } catch (e: unknown) {
      const apiErrors = revealContactsFieldErrorsFromApi((e as { data?: unknown }).data)
      if (Object.keys(apiErrors).length > 0) {
        setExecutorFieldErrors(apiErrors)
      }
    } finally {
      setIsSavingExecutor(false)
    }
  }

  const handleSendContacts = async () => {
    const validationErrors = validateRevealContactsForm({ form: contactForm, t })
    if (Object.keys(validationErrors).length > 0) {
      setExecutorFieldErrors(validationErrors)
      setContactSubmitError(null)
      return
    }

    setExecutorFieldErrors({})
    setContactSubmitError(null)
    setIsRevealingContacts(true)
    try {
      await onRevealContacts()
    } catch (e: unknown) {
      const apiErrors = revealContactsFieldErrorsFromApi((e as { data?: unknown }).data)
      if (Object.keys(apiErrors).length > 0) {
        setExecutorFieldErrors(apiErrors)
        return
      }
      setContactSubmitError(formatApplicationActionError({
        data: (e as { data?: unknown }).data,
        fallback: t('applications__text__actionError'),
      }))
    } finally {
      setIsRevealingContacts(false)
    }
  }

  if (!managerView || !app.manager_id) {
    return null
  }

  const contactsRevealed = Boolean(app.contacts_revealed_at)

  return (
    <Box className='application-manager-contacts-section'>
      {showClientBlock && (
        <ApplicationContactsBlock
          title={t('applications__text__mgrClientContactsTitle')}
          fields={buildClientContactFields(clientContactForm, t)}
          editable
          fieldErrors={clientFieldErrors}
          onFieldChange={handleClientFieldChange}
          onSave={handleSaveClient}
          saveLabel={t('applications__text__saveClientContacts')}
          saveDisabled={!clientDirty}
          saving={isSavingClient}
        />
      )}

      {showExecutorBlock ? (
        <>
          <ApplicationContactsBlock
            title={t('applications__text__mgrExecutorContactsTitle')}
            fields={buildExecutorContactFields(showExecutorEditor ? contactForm : executorContactFormFromApp(app), t)}
            editable={showExecutorEditor}
            fieldErrors={executorFieldErrors}
            onFieldChange={handleExecutorFieldChange}
            onSave={showExecutorEditor ? handleSaveExecutor : undefined}
            saveLabel={t('applications__text__saveExecutorContacts')}
            saveDisabled={!executorDirty}
            saving={isSavingExecutor}
          />
          {!contactsRevealed && showManagerRevealContacts({ app, managerView }) && (
            <>
              {contactSubmitError && (
                <Alert severity='error'>
                  <Typography component='span' variant='inherit'>
                    {contactSubmitError}
                  </Typography>
                </Alert>
              )}
              <Button
                variant='contained'
                disabled={isRevealingContacts}
                onClick={() => handleSendContacts()}
              >
                <Typography component='span' variant='inherit'>
                  {t('applications__text__sendContacts')}
                </Typography>
              </Button>
            </>
          )}
        </>
      ) : (
        <Typography variant='body2' color='text.primary'>
          {t('applications__text__executorContactsNotProvided')}
        </Typography>
      )}
    </Box>
  )
}

export default ManagerContactsSection
